import React from 'react';
import { shape, arrayOf, func, string, bool, object, number, oneOfType } from 'prop-types';
import Icon from 'components/icon';
import KeywordFilter from './keyword-filter/';
import MultiCheckboxFilter from './multi-checkbox-filter/';
import SingleCheckboxFilter from './single-checkbox-filter/';
import TagFilter from './tag-filter/';
import TabFilter from './tab-filter/';
import DateRangeFilter from './date-range-filter/';
import * as FilterTypeConstants from '../constants/filter-types';
import { getActiveValues } from '../utils/active-filters';
import TextBlock from '../../text-block';

export default function FiltersSection({
    filters, filterSettings, activeFilters, toggleFilters, resetFilterGroup, focusedFilter,
    toggleFocus, maxDate, resetFilters
}) {
    let activeFilterTxt = [];

    if (
        activeFilters.length > 0 &&
        activeFilters[0].textBlock !== undefined &&
        activeFilters[0].textBlock !== null
    ) {
        activeFilterTxt = activeFilters[0].textBlock.filter(t =>
            activeFilters[0].values[0].toUpperCase() === t.values.toUpperCase());
    }

    let textBlock = '';

    if (activeFilters.length > 0) {
        textBlock = activeFilterTxt.length ? activeFilterTxt[0].text : '';
    } else {
        textBlock = filters[0].defaultText;
    }

    const filterClass = `filters${textBlock ? '' : ' no-text-block'}`;

    return (
        <div className="filters-section">
            <div className="filters-container">
                <div className={filterClass}>
                    {filterSettings.filterLabel ? <h3>{filterSettings.filterLabel}</h3> : ''}
                    {textBlock !== '' &&
                        <TextBlock
                            text={textBlock}
                        />
                    }
                    <div className="filters-reset-header">
                        <div className="filters-reset-container">
                            <div className="filters-reset-label">
                                Filter by:
                            </div>
                            <button className="filters-reset-btn" onClick={resetFilters}>
                                <Icon glyph="reset" />
                                <span>Clear</span>
                            </button>
                        </div>
                    </div>
                    {!filterSettings.hideInputFilter && filterSettings.hideInputFilter !== undefined ?
                        <div className="input-filter-wrapper">
                            {filters.map(filter => {
                                const activeValues = getActiveValues(filter, activeFilters);
                                switch (filter.type) {
                                    case FilterTypeConstants.FILTER_TYPE_KEYWORDS:
                                        return (
                                            <KeywordFilter
                                                key={filter.code}
                                                filter={filter}
                                                keyword={activeValues.length ? activeValues[0] : ''}
                                                updateKeyword={toggleFilters}
                                                placeholderText={filterSettings.keywordPlaceholderText ? filterSettings.keywordPlaceholderText : 'Enter keywords'}
                                                classModifier="filter-item"
                                            />
                                        );

                                    case FilterTypeConstants.FILTER_TYPE_MULTI_CHECKBOX:
                                        return (
                                            <MultiCheckboxFilter
                                                key={filter.code}
                                                filter={filter}
                                                selectedItems={activeValues}
                                                toggleFilters={toggleFilters}
                                                classModifier="filter-item"
                                                isFocused={focusedFilter === filter.code}
                                                toggleFocus={toggleFocus}
                                            />
                                        );

                                    case FilterTypeConstants.FILTER_TYPE_SINGLE_CHECKBOX:
                                        return (
                                            <SingleCheckboxFilter
                                                key={filter.code}
                                                filter={filter}
                                                isChecked={activeValues.length ?
                                                    (filter.values[0] === activeValues[0]) : false}
                                                toggleFilters={toggleFilters}
                                                classModifier="filter-item"
                                            />
                                        );

                                    case FilterTypeConstants.FILTER_TYPE_DATE_RANGE:
                                        return (
                                            <DateRangeFilter
                                                key={filter.code}
                                                updateKeyword={toggleFilters}
                                                filter={filter}
                                                placeholderText={filter.label ? filter.label : ''}
                                                classModifier="filter-item"
                                                maxDate={maxDate}
                                                toggleFilters={toggleFilters}
                                                selectedItems={activeValues}
                                                limitedDate={filterSettings.limitedDate}
                                            />
                                        );

                                    default:
                                        return null;
                                }
                            })}
                        </div> : ''
                    }

                    {filters.map(filter => {
                        const activeValues = getActiveValues(filter, activeFilters);
                        switch (filter.type) {
                            case FilterTypeConstants.FILTER_TYPE_TAGS:
                                return (
                                    <TagFilter
                                        key={filter.code}
                                        filter={filter}
                                        selectedItems={activeValues}
                                        toggleFilters={toggleFilters}
                                        resetFilterGroup={resetFilterGroup}
                                        classModifier="filter-item"
                                        isFocused={focusedFilter === filter.code}
                                        toggleFocus={toggleFocus}
                                    />
                                );
                            case FilterTypeConstants.FILTER_TYPE_TABS:
                                return (
                                    <TabFilter
                                        key={filter.code}
                                        filter={filter}
                                        selectedItems={activeValues}
                                        toggleFilters={toggleFilters}
                                        resetFilterGroup={resetFilterGroup}
                                        classModifier="filter-item"
                                    />
                                );
                            default:
                                return null;
                        }
                    })}
                </div>
            </div>
        </div>
    );
}

FiltersSection.propTypes = {
    /* eslint-disable react/no-typos */
    // The disable is needed until https://github.com/yannickcr/eslint-plugin-react/issues/1389 is resolved
    filters: arrayOf(shape({
        code: string.isRequired,
        label: string.isRequired,
        values: arrayOf(oneOfType([string, bool, number]))
    })).isRequired,
    filterSettings: shape({
        itemsPerPage: number,
        showResultCount: bool,
        showActiveFilters: bool,
        activeFiltersStyle: string,
        activeFiltersLabel: string,
        keywordPlaceholderText: string,
        keywordSearchableFields: arrayOf(string),
        sortBy: arrayOf(object)
    }).isRequired,
    activeFilters: arrayOf(shape({
        code: string.isRequired,
        label: string.isRequired,
        values: arrayOf(oneOfType([string, bool, number])).isRequired
    })).isRequired,
    focusedFilter: string,
    toggleFilters: func.isRequired,
    resetFilterGroup: func.isRequired,
    toggleFocus: func.isRequired,
    maxDate: number.isRequired,
    resetFilters: func.isRequired
    /* eslint-enable react/no-typos */
};

FiltersSection.defaultProps = { focusedFilter: null };
